import React, { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import IconButton from '@material-ui/core/IconButton';
import { View } from 'components/layout/View';
import Text from './Text';
import useI18Language from 'hooks/useI18Language';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  blurred: {
    filter: 'blur(6px)',
    transition: 'filter 0.2s ease-in-out',
    userSelect: 'none',
  },
  normal: {
    filter: 'none',
    transition: 'filter 0.2s ease-in-out',
  },
  eyeButton: {
    padding: 4,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

const SensitiveText = ({
  children,
  style = {},
  hideByDefault = true,
  showToggle = true,
  toggleSize = 'small',
  maskMode = 'blur', // 'blur' or 'dots'
  showLastDigits = 0, // Number of characters to show at the end
  maskChar = '•', // Character to use for masking
  maxMaskLength = 4, // Maximum number of mask characters to show
  align = 'left', // Text alignment
  ...props
}) => {
  const classes = useStyles();
  const [isHidden, setIsHidden] = useState(hideByDefault);
  const { getI18Translation } = useI18Language();

  const toggleVisibility = () => {
    setIsHidden(prev => !prev);
  };

  const getMaskedText = (text) => {
    if (!text || typeof text !== 'string') return text;

    if (showLastDigits > 0 && text.length > showLastDigits) {
      const hiddenCharsCount = text.length - showLastDigits;
      const maskLength = Math.min(hiddenCharsCount, maxMaskLength);
      const maskedPart = maskChar.repeat(maskLength);
      const visiblePart = text.slice(-showLastDigits);
      return maskedPart + visiblePart;
    }

    const maskLength = Math.min(text.length, maxMaskLength);
    return maskChar.repeat(maskLength);
  };

  const displayText = (maskMode === 'dots' && isHidden) 
    ? getMaskedText(children) 
    : children;

  const textStyle = (maskMode === 'blur' && isHidden) ? classes.blurred : classes.normal;

  if (!showToggle) {
    // If no toggle, just show the text based on hideByDefault
    return (
      <Text
        className={textStyle}
        style={style}
        {...props}
      >
        {displayText}
      </Text>
    );
  }

  return (
    <View 
      fD="row" 
      aI="center" 
      jC={align === 'right' ? 'flex-end' : 'flex-start'}
      w="100%"
      className={classes.container}
    >
      <View
        fD="row"
        aI="center"
        className={classes.container}
        style={{ gap: 'inherit' }}
      >
        <Text
          className={textStyle}
          style={style}
          align={align}
          {...props}
        >
          {displayText}
        </Text>
        <IconButton
          size={toggleSize}
          onClick={toggleVisibility}
          className={classes.eyeButton}
          title={isHidden ? getI18Translation('show_sensitive_data') : getI18Translation('hide_sensitive_data')}
        >
          {isHidden ? 
            <VisibilityIcon fontSize="small" color="action" /> : 
            <VisibilityOffIcon fontSize="small" color="action" />
          }
        </IconButton>
      </View>
    </View>
  );
};

export default SensitiveText;